/**
 * Project lifecycle + workflow enums. Mirrored across mobile +
 * web — define once here.
 *
 * `ProjectStatus` is the lifecycle state (active / on hold /
 * completed / archived). `ProjectPhase` is the workflow stage
 * within an Active project (design / 2D / 3D / execution /
 * handover). Distinct concepts — a project can be both Active
 * AND in the Design phase.
 */

export type ProjectStatus =
  | 'active'
  | 'on_hold'
  | 'completed'
  | 'archived';

export const PROJECT_STATUS_OPTIONS: { key: ProjectStatus; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'on_hold', label: 'On Hold' },
  { key: 'completed', label: 'Completed' },
  { key: 'archived', label: 'Archived' },
];

/** Top-level typology — what kind of space is the studio
 *  fitting out. */
export type ProjectTypology =
  | 'residential'
  | 'commercial'
  | 'hospitality'
  | 'industrial'
  | 'other';

export const PROJECT_TYPOLOGIES: { key: ProjectTypology; label: string }[] = [
  { key: 'residential', label: 'Residential' },
  { key: 'commercial', label: 'Commercial' },
  { key: 'hospitality', label: 'Hospitality' },
  { key: 'industrial', label: 'Industrial' },
  { key: 'other', label: 'Other' },
];

/**
 * Workflow phase for an Active project — the *production stage*
 * the studio is currently in, distinct from lifecycle `status`.
 * Optional on the doc; UI shows "Set phase" until picked.
 *
 * Five-step interior-design workflow:
 *   design       → ideation, mood-board, concept
 *   drawing_2d   → 2D drawings / floor plans
 *   render_3d    → 3D renders / visualisation
 *   execution    → on-site build / fit-out
 *   handover     → snagging + client handover
 */
export type ProjectPhase =
  | 'design'
  | 'drawing_2d'
  | 'render_3d'
  | 'execution'
  | 'handover';

export const PROJECT_PHASES: { key: ProjectPhase; label: string }[] = [
  { key: 'design', label: 'Design' },
  { key: 'drawing_2d', label: '2D Drawing' },
  { key: 'render_3d', label: '3D Render' },
  { key: 'execution', label: 'Execution' },
  { key: 'handover', label: 'Handover' },
];

/** Display label for a phase key — falls back to "Set phase"
 *  for null/undefined (the "no phase chosen yet" state). */
export function phaseLabel(p: ProjectPhase | null | undefined): string {
  if (!p) return 'Set phase';
  return PROJECT_PHASES.find((x) => x.key === p)?.label ?? p;
}
